import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, AlertTriangle, Scale, Ban } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';

export function TermsOfService() {
  const lastUpdated = "January 29, 2026";
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/login">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <h1 className="font-serif text-xl font-medium">Terms of Service</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="prose prose-sm max-w-none">
          <p className="text-muted-foreground text-sm mb-8">
            Last updated: {lastUpdated}
          </p>

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="font-serif text-2xl font-medium mb-4">Agreement to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing or using MindDeck ("Service"), you agree to be bound by these Terms of Service 
              ("Terms"). If you disagree with any part of the terms, you may not access the Service.
            </p>
          </section>

          <Separator className="my-8" />

          {/* Description */}
          <section className="mb-8">
            <h2 className="font-serif text-2xl font-medium mb-4">Description of Service</h2>
            <p className="text-muted-foreground leading-relaxed">
              MindDeck is a knowledge management application that allows users to create, organize, and 
              share collections of cards containing information. The Service includes features for creating 
              cards with custom fields, organizing cards into decks and packets, sharing decks with others, 
              and integrating with third-party services.
            </p>
          </section>

          <Separator className="my-8" />

          {/* Account */}
          <section className="mb-8">
            <h2 className="font-serif text-2xl font-medium mb-4">User Accounts</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>You must provide accurate and complete information when creating an account</li>
              <li>You are responsible for maintaining the security of your account credentials</li>
              <li>You must notify us immediately of any unauthorized access to your account</li>
              <li>You may not share your account credentials with others</li>
              <li>You must be at least 13 years old to use this Service</li>
            </ul>
          </section>

          <Separator className="my-8" />

          {/* User Content */}
          <section className="mb-8">
            <h2 className="font-serif text-2xl font-medium mb-4">User Content</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You retain ownership of any content you create using the Service ("User Content"). By 
              creating User Content, you grant us a license to store, display, and process your content 
              as necessary to provide the Service.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              When you share content publicly or with other users, you grant them the right to view and 
              import that content according to the sharing settings you choose.
            </p>
          </section>

          <Separator className="my-8" />

          {/* Prohibited Uses */}
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Ban className="h-5 w-5 text-primary" />
              <h2 className="font-serif text-2xl font-medium">Prohibited Uses</h2>
            </div>
            <p className="text-muted-foreground mb-4">You agree not to use the Service to:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on the intellectual property rights of others</li>
              <li>Upload malicious code, viruses, or harmful content</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Scrape or collect data from other users without consent</li>
            </ul>
          </section>

          <Separator className="my-8" />

          {/* Third Party Services */}
          <section className="mb-8">
            <h2 className="font-serif text-2xl font-medium mb-4">Third-Party Services</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Service may integrate with third-party services such as Google, Airtable, and others. 
              Your use of these integrations is subject to the respective third party's terms of service 
              and privacy policies. We are not responsible for the content, privacy practices, or 
              availability of these external services.
            </p>
          </section>

          <Separator className="my-8" />

          {/* Disclaimer */}
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="h-5 w-5 text-primary" />
              <h2 className="font-serif text-2xl font-medium">Disclaimer of Warranties</h2>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER 
              EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, 
              OR SECURE. YOU USE THE SERVICE AT YOUR OWN RISK.
            </p>
          </section>

          <Separator className="my-8" />

          {/* Limitation of Liability */}
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Scale className="h-5 w-5 text-primary" />
              <h2 className="font-serif text-2xl font-medium">Limitation of Liability</h2>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
              SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR DATA, ARISING OUT OF 
              OR IN CONNECTION WITH YOUR USE OF THE SERVICE.
            </p>
          </section>

          <Separator className="my-8" />

          {/* Termination */}
          <section className="mb-8">
            <h2 className="font-serif text-2xl font-medium mb-4">Termination</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may terminate or suspend your account and access to the Service immediately, without 
              prior notice, for conduct that we believe violates these Terms or is harmful to other users, 
              us, or third parties. You may terminate your account at any time by contacting us.
            </p>
          </section>

          <Separator className="my-8" />

          {/* Changes */}
          <section className="mb-8">
            <h2 className="font-serif text-2xl font-medium mb-4">Changes to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to modify these Terms at any time. We will provide notice of significant 
              changes by posting the new Terms on this page and updating the "Last updated" date. Your 
              continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <Separator className="my-8" />

          {/* Contact */}
          <section className="mb-8">
            <h2 className="font-serif text-2xl font-medium mb-4">Contact Information</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about these Terms, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
              <p className="text-sm">
                <strong>Email:</strong> <a href="mailto:emaildtdm@gmail.com" className="text-primary hover:underline">emaildtdm@gmail.com</a>
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} MindDeck. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default TermsOfService;
